import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';

import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import LoadingContainer from 'src/componentes/loading-container';
import Confirm from 'src/componentes/message-box/confirm';
import ColaboradorEdit from 'src/paginas/colaborador/edit';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';

import {
  PARTICIPANTE_STATUS,
  PARTICIPANTES_FILTRO_CONFIRM_LIMIT,
  PARTICIPANTES_FILTRO_MAX_LIMIT,
} from './constants';
import ParticipantesFiltroDrawer from './ParticipantesFiltroDrawer';
import ParticipantesList from './ParticipantesList';
import {
  createEmptyFiltro,
  getAreaNome,
  getCargoNome,
  getParticipantesResponseCount,
  limitParticipantesPorFiltro,
  mergeParticipantes,
  normalizeFiltroSearch,
  normalizeParticipantesResponse,
  splitColaboradoresGrupos,
} from './utils';

const ParticipantesPesquisaClimaTab = forwardRef(({ active, disabled, entidadeId, filtroDrawerFocusTrapPriority }, ref) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const participantesRef = useRef([]);
  const hasLoadedRef = useRef(false);
  const [participantes, setParticipantes] = useState([]);
  const [filtro, setFiltro] = useState(createEmptyFiltro());
  const [participantesSearchText, setParticipantesSearchText] = useState('');
  const [participantesPageSize, setParticipantesPageSize] = useState(10);
  const [participantesCurrentPage, setParticipantesCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const isEntidadePersisted = !!entidadeId;

  const updateParticipantes = (nextParticipantes) => {
    participantesRef.current = nextParticipantes;
    setParticipantes(nextParticipantes);
  };

  const loadParticipantes = async (targetEntidadeId = entidadeId) => {
    if (!targetEntidadeId) {
      updateParticipantes([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await http.get('/PesquisaClimaParticipantes/Obter', {
        params: {
          pesquisaClimaId: targetEntidadeId,
          pageSize: 10000,
          page: 0,
        },
      });

      updateParticipantes(
        normalizeParticipantesResponse(response.data, PARTICIPANTE_STATUS.SALVO)
      );
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (active && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadParticipantes();
    }
  }, [active]);

  useEffect(() => {
    if (hasLoadedRef.current) {
      loadParticipantes();
    }
  }, [entidadeId]);

  const persistParticipantes = async (
    nextParticipantes = participantesRef.current,
    targetEntidadeId = entidadeId
  ) => {
    if (!targetEntidadeId) return null;

    const response = await http.post(
      '/PesquisaClimaParticipantes/SalvarParticipantes',
      {
        pesquisaClimaId: targetEntidadeId,
        participantes: nextParticipantes.map((participante) => ({
          id: participante.id,
        })),
      }
    );

    return response.data;
  };

  const getStatusParticipanteLabel = useCallback(
    (item) =>
      item.statusSalvamento === PARTICIPANTE_STATUS.PENDENTE
        ? intl.formatMessage({ id: 'labelPendenteSalvamento' })
        : intl.formatMessage({ id: 'salvo' }),
    [intl]
  );

  const filterParticipantesBySearch = useCallback(
    (items = participantes, textoPesquisa = participantesSearchText) => {
      const searchText = normalizeFiltroSearch(textoPesquisa);

      if (!searchText) return items;

      return items.filter((item) =>
        [
          item.nome,
          getAreaNome(item),
          getCargoNome(item),
          getStatusParticipanteLabel(item),
        ]
          .map(normalizeFiltroSearch)
          .some((value) => value.includes(searchText))
      );
    },
    [getStatusParticipanteLabel, participantes, participantesSearchText]
  );

  const participantesFiltrados = useMemo(
    () => filterParticipantesBySearch(participantes, participantesSearchText),
    [filterParticipantesBySearch, participantes, participantesSearchText]
  );

  const participantesPaginados = useMemo(() => {
    const startIndex = (participantesCurrentPage - 1) * participantesPageSize;

    return participantesFiltrados.slice(
      startIndex,
      startIndex + participantesPageSize
    );
  }, [participantesFiltrados, participantesCurrentPage, participantesPageSize]);

  const participantesPageCount = useMemo(
    () =>
      Math.max(
        Math.ceil(participantesFiltrados.length / participantesPageSize),
        1
      ),
    [participantesFiltrados.length, participantesPageSize]
  );

  useEffect(() => {
    if (participantesCurrentPage > participantesPageCount) {
      setParticipantesCurrentPage(participantesPageCount);
    }
  }, [participantesCurrentPage, participantesPageCount]);

  const mergeParticipantesResolvidos = (
    participantesResolvidos,
    closeFilter = false
  ) => {
    updateParticipantes(
      mergeParticipantes(participantesRef.current, participantesResolvidos)
    );

    if (closeFilter) {
      setFilterOpen(false);
    }
  };

  const handleConfirmarAdicionarParticipantesResolvidos = (
    participantesResolvidos,
    totalRegistros,
    closeFilter = false
  ) => {
    if (totalRegistros <= PARTICIPANTES_FILTRO_CONFIRM_LIMIT) {
      mergeParticipantesResolvidos(participantesResolvidos, closeFilter);
      return;
    }

    ContentManager.addContent(
      <Confirm
        zIndex={12001}
        focusTrapPriority={(filtroDrawerFocusTrapPriority ?? 2) + 1}
        message={
          <div>
            <div>
              {intl.formatMessage(
                {
                  id: 'labelConfirmarAdicionarParticipantesPorFiltroPesquisaClima',
                },
                {
                  total: totalRegistros,
                }
              )}
            </div>
            {totalRegistros > PARTICIPANTES_FILTRO_MAX_LIMIT && (
              <div className="mt-2">
                {intl.formatMessage(
                  {
                    id: 'labelLimiteAdicionarParticipantesPorFiltroPesquisaClima',
                  },
                  {
                    limite: PARTICIPANTES_FILTRO_MAX_LIMIT,
                  }
                )}
              </div>
            )}
          </div>
        }
        labelConfirm={intl.formatMessage({ id: 'adicionar' })}
        handleConfirm={() =>
          mergeParticipantesResolvidos(participantesResolvidos, closeFilter)
        }
      />
    );
  };

  const obterParticipantesPorFiltro = async (model) => {
    const response = await http.post(
      '/PesquisaClimaParticipantes/ObterParticipantesPorFiltro',
      {
        ...model,
        pesquisaClimaId: entidadeId,
        pageSize: PARTICIPANTES_FILTRO_MAX_LIMIT,
        page: 0,
      }
    );

    const participantesResolvidos = normalizeParticipantesResponse(
      response.data,
      PARTICIPANTE_STATUS.PENDENTE
    );

    return {
      participantes: limitParticipantesPorFiltro(participantesResolvidos),
      total: getParticipantesResponseCount(
        response.data,
        participantesResolvidos.length
      ),
    };
  };

  const handleAplicarFiltro = async () => {
    const { colaboradores, grupos } = splitColaboradoresGrupos(
      filtro.colaboradoresGrupos
    );

    setIsLoading(true);
    try {
      const resultado = await obterParticipantesPorFiltro({
        tempoCargoDias: filtro.tempoCargoDias,
        tempoEmpresaDias: filtro.tempoEmpresaDias,
        perfilLideranca: filtro.perfilLideranca,
        cargos: filtro.cargos,
        areas: filtro.areas,
        considerarAreasSubordinadas: filtro.considerarAreasSubordinadas,
        colaboradores,
        grupos,
      });

      handleConfirmarAdicionarParticipantesResolvidos(
        resultado.participantes,
        resultado.total,
        true
      );
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeFiltro = (field, value) => {
    setFiltro((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFiltro = () => {
    setFiltro(createEmptyFiltro());
  };

  const restoreParticipante = (participante, index) => {
    const participanteJaExiste = participantesRef.current.some(
      (item) => item.id === participante.id
    );

    if (participanteJaExiste) return;

    const nextParticipantes = [...participantesRef.current];
    const insertIndex = Math.min(index, nextParticipantes.length);

    nextParticipantes.splice(insertIndex, 0, participante);
    updateParticipantes(nextParticipantes);
  };

  const showRemoveParticipanteToast = (participante, index) => {
    enqueueSnackbar(
      intl.formatMessage(
        { id: 'voceDeletouColaborador' },
        { colaborador: resources.colaborador }
      ),
      {
        autoHideDuration: 6000,
        variant: 'warning',
        action: (
          <Button
            color="secondary"
            style={{ color: '#fff', border: '1px solid #fff' }}
            outline
            onClick={() => {
              restoreParticipante(participante, index);
              closeSnackbar();
            }}
          >
            {intl.formatMessage({ id: 'reverter' })}
          </Button>
        ),
      }
    );
  };

  const removeParticipante = (participanteId) => {
    const participanteIndex = participantesRef.current.findIndex(
      (item) => item.id === participanteId
    );
    const participanteRemovido = participantesRef.current[participanteIndex];

    if (!participanteRemovido) return;

    updateParticipantes(
      participantesRef.current.filter((item) => item.id !== participanteId)
    );

    showRemoveParticipanteToast(participanteRemovido, participanteIndex);
  };

  const handleRemoveParticipante = (participanteId) => {
    if (disabled) return;

    ContentManager.addContent(
      <Confirm
        title={intl.formatMessage(
          {
            id: 'labelExcluirParticipantePesquisaClima',
          },
          { pesquisa: resources.pesquisa }
        )}
        message={intl.formatMessage(
          {
            id: 'labelExcluirParticipantePesquisaClimaDescricao',
          },
          { pesquisa: resources.pesquisa }
        )}
        labelConfirm={intl.formatMessage({ id: 'label.excluir' })}
        handleConfirm={() => removeParticipante(participanteId)}
        textCapitalizeTitle={false}
        titleCapitalizeNone
      />
    );
  };

  const handleClearParticipantes = () => {
    if (disabled || participantesRef.current.length === 0) return;
    
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'label.desejaMesmoExcluirTodosOsRegistros',
        })}
        handleConfirm={() => updateParticipantes([])}
      />
    );
  };

  const handleOpenColaborador = (colaborador, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!colaborador?.id) return;

    ContentManager.addContent(
      <ColaboradorEdit
        disabledEdit
        item={colaborador}
        showDelete={false}
        showIconDelete={false}
        showSave={false}
        showSaveClose={false}
      />
    );
  };

  const localSearchParticipantes = ({ textoPesquisa = '' }) => {
    if (textoPesquisa !== participantesSearchText) {
      setParticipantesSearchText(textoPesquisa);
      setParticipantesCurrentPage(1);
    }

    const result = filterParticipantesBySearch(
      participantesRef.current,
      textoPesquisa
    ).slice(0, participantesPageSize);

    setIsSearchLoading(false);

    return result;
  };

  const handleChangePageSize = (pageSize) => {
    setParticipantesPageSize(pageSize);
    setParticipantesCurrentPage(1);
  };

  useImperativeHandle(ref, () => ({
    loadParticipantes,
    getParticipantesCount: () => participantesRef.current.length,
    mergeParticipantes: (items) => {
      if (disabled) return;

      updateParticipantes(
        mergeParticipantes(
          participantesRef.current,
          normalizeParticipantesResponse(items, PARTICIPANTE_STATUS.PENDENTE)
        )
      );
    },
    saveParticipantes: async (savedEntidadeId) => {
      const targetEntidadeId = savedEntidadeId || entidadeId;

      if (disabled) return null;

      if (!hasLoadedRef.current) return null;

      if (!targetEntidadeId) return null;

      const response = await persistParticipantes(
        participantesRef.current,
        targetEntidadeId
      );

      updateParticipantes(
        participantesRef.current.map((item) => ({
          ...item,
          statusSalvamento: PARTICIPANTE_STATUS.SALVO,
        }))
      );

      return response;
    },
  }));

  const renderBlockedMessage = () => (
    <div className="d-flex align-items-center justify-content-center text-muted py-5">
      {intl.formatMessage(
        { id: 'labelSalvePesquisaClimaAntesEditarParticipantes' },
        { pesquisa: resources.pesquisa }
      )}
    </div>
  );

  const renderParticipantes = () => (
    <>
      <ParticipantesList
        currentPage={participantesCurrentPage}
        disabled={disabled}
        entidadeId={entidadeId}
        getStatusParticipanteLabel={getStatusParticipanteLabel}
        localSearchParticipantes={localSearchParticipantes}
        onChangePage={setParticipantesCurrentPage}
        onChangePageSize={handleChangePageSize}
        onClearParticipantes={handleClearParticipantes}
        onOpenColaborador={handleOpenColaborador}
        onOpenFiltro={() => setFilterOpen(true)}
        onRemoveParticipante={handleRemoveParticipante}
        onSearchChangeStart={() => setIsSearchLoading(true)}
        pageSize={participantesPageSize}
        participantes={participantes}
        participantesFiltrados={participantesFiltrados}
        participantesSearchLoading={isSearchLoading}
        participantesPaginados={participantesPaginados}
      />
      <ParticipantesFiltroDrawer
        filtro={filtro}
        focusTrapPriority={filtroDrawerFocusTrapPriority}
        onAplicarFiltro={handleAplicarFiltro}
        onChangeFiltro={handleChangeFiltro}
        onClearFiltro={handleClearFiltro}
        onClose={() => setFilterOpen(false)}
        open={filterOpen}
      />
    </>
  );

  return (
    <LoadingContainer isLoading={isLoading}>
      {isEntidadePersisted ? renderParticipantes() : renderBlockedMessage()}
    </LoadingContainer>
  );
});

export default ParticipantesPesquisaClimaTab;
